import { Fragment, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { Scales } from '@phosphor-icons/react'
import { useFormat } from '@/i18n'
import { Banner } from '@/ui'
import { useClearHold, useLot, type LotDetail } from '../api/gate'
import { useCustody } from '../api/store'
import { useCan } from '@/auth/context'
import { ActionButton } from '../components/Can'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { CorrectionDialog } from '../components/CorrectionDialog'
import { DisputeSettlePanel } from '../components/ExceptionActions'
import { ExceptionTable } from '../components/ExceptionTable'
import { ClearBlockersNote, HoldActions } from '../components/HoldActions'
import { ConsolePage, DetailColumns, QueryState } from '../components/Layout'
import { PhotoViewer } from '../components/PhotoViewer'
import { Timeline, type TimelineItem } from '../components/Timeline'
import {
  Code,
  Facts,
  Kg,
  Money,
  RefLink,
  Section,
  Status,
  Stream,
  UserName,
  When,
} from '../components/bits'
import { CUSTODY_REASON_LABEL, exceptionLabel, humanize } from '../labels'
import { useScope } from '../api/appConfig'
import { payHoldLabel } from '../api/payouts'
import { lotHoldLabel } from '../labels'

/** Lot view (F4.10-e/h/j, F4-B2.d-c, F5.9-b): bags, weights, sample, slip, money, holds, custody. */
export function LotDetailPage() {
  const { id } = useParams()
  const q = useLot(id)
  const lot = q.data
  return (
    <ConsolePage
      back={{ to: '/c/lots', label: 'Lots' }}
      eyebrow="Lot"
      title={lot ? <span className="font-mono">{lot.code}</span> : 'Lot'}
      description={
        lot && (
          <span className="flex flex-wrap items-center gap-1.5">
            <Stream stream={lot.stream} />
            <Status status={lot.status} />
            {lot.hold !== 'NONE' && (
              <Status
                status={lot.hold}
                label={`On hold · ${lot.holdReasons.map(lotHoldLabel).join(', ') || lot.hold}`}
              />
            )}
            {lot.paperDay && <Status status="PAPER" />}
          </span>
        )
      }
      actions={lot && <LotActions lot={lot} />}
    >
      <QueryState query={q} label="the lot">
        {lot && <LotBody lot={lot} />}
      </QueryState>
    </ConsolePage>
  )
}

function LotActions({ lot }: { lot: LotDetail }) {
  // ?correct=1 opens the correction straight away (e.g. from a paper-day payout's "Correct weight").
  const [params, setParams] = useSearchParams()
  const [correct, setCorrectState] = useState(params.get('correct') === '1')
  const setCorrect = (open: boolean) => {
    setCorrectState(open)
    if (!open && params.has('correct')) {
      params.delete('correct')
      setParams(params, { replace: true })
    }
  }
  const committed = Boolean(lot.committedAt) && lot.status !== 'VOID'
  return (
    <>
      <HoldActions lot={lot} size="md" />
      {committed && (
        <ActionButton
          perm="units:correct"
          leadingIcon={<Scales />}
          onClick={() => setCorrect(true)}
        >
          Correct a bag
        </ActionButton>
      )}
      <CorrectionDialog open={correct} onClose={() => setCorrect(false)} lot={lot} />
    </>
  )
}

/**
 * A bag's own quarantine (stray in a count, found after missing, a replacement, loaded twice…):
 * POST /containers/:id/hold/clear — 409 while an exception on the bag is open.
 */
function BagHoldClear({ bagId, tag }: { bagId: string; tag: string }) {
  const [open, setOpen] = useState(false)
  const clear = useClearHold()
  return (
    <>
      <ActionButton
        perm="units:clear-hold"
        hide
        size="sm"
        variant="ghost"
        onClick={() => setOpen(true)}
      >
        Clear
      </ActionButton>
      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        title={`Clear the hold on bag ${tag}`}
        description="Only the bag's own hold is lifted. Open exceptions on the bag must be resolved first."
        confirmLabel="Clear bag hold"
        loading={clear.isPending}
        reason={{ label: 'Note', required: false, placeholder: 'What was checked' }}
        onConfirm={(note) =>
          clear.mutateAsync({ id: bagId, note, subject: 'containers' }).then(() => setOpen(false))
        }
      />
    </>
  )
}

function LotBody({ lot }: { lot: LotDetail }) {
  const fmt = useFormat()
  const { v1 } = useScope()
  const can = useCan()
  const [openBag, setOpenBag] = useState<string | null>(null)
  const openDisputes = lot.exceptions.filter((e) => e.type === 'DISPUTE' && e.status === 'OPEN')
  const homeStorage = lot.declarations.find((d) => d.formCode === 'HOME_STORAGE')
  return (
    <DetailColumns
      main={
        <>
          {lot.hold === 'QUARANTINE' && lot.clearBlockers && (
            <ClearBlockersNote blockers={lot.clearBlockers} unitId={lot.id} />
          )}

          {openDisputes.map((d) => (
            <Section key={d.id} title="Dispute · settle" className="border-warn/40">
              <DisputeSettlePanel exception={{ ...d, raisedById: null }} />
            </Section>
          ))}

          <Section
            title="Figures"
            eyebrow={lot.rateCardVersion ? `Rate card v${lot.rateCardVersion}` : undefined}
          >
            <Facts
              columns={4}
              items={[
                { label: 'Bags', value: lot.bagCount, mono: true },
                { label: 'Gross', value: <Kg grams={lot.grossG} /> },
                { label: 'Sacks', value: <Kg grams={lot.tareG} /> },
                { label: 'Net', value: <Kg grams={lot.netG} /> },
                {
                  label: 'Moisture',
                  value: lot.moisturePct ? `${lot.moisturePct} %` : '—',
                  mono: true,
                },
                // V1 cards have no deductions: payable is the net weight.
                ...(v1
                  ? []
                  : [
                      {
                        label: 'Deduction',
                        value: lot.deductionPct ? `${lot.deductionPct} %` : '0 %',
                        mono: true,
                      },
                      { label: 'Payable', value: <Kg grams={lot.payableG} /> },
                    ]),
                {
                  label: 'Rate',
                  value:
                    lot.ratePaisePerKg !== null ? <>{fmt.money(lot.ratePaisePerKg)}/kg</> : '—',
                  mono: true,
                },
                {
                  label: 'Lot value',
                  value: <Money paise={lot.amountPaise} className="font-semibold" />,
                },
                { label: 'Ledger balance', value: <Money paise={lot.ledgerBalancePaise} /> },
                { label: 'Season', value: lot.seasonCode ?? '—', mono: true },
                { label: 'Committed', value: <When at={lot.committedAt} /> },
              ]}
            />
          </Section>

          <Section
            title="Bags and every weight"
            eyebrow={`${lot.bags.length} bags`}
            bodyClassName="p-0"
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[620px] text-[14px]">
                <caption className="sr-only">Bags of the lot</caption>
                <thead className="font-mono text-[10.5px] uppercase tracking-label text-muted">
                  <tr className="border-b border-rule">
                    <th scope="col" className="px-4 py-2 text-left">
                      Tag
                    </th>
                    <th scope="col" className="px-2 text-left">
                      Sack
                    </th>
                    <th scope="col" className="px-2 text-right">
                      Gross
                    </th>
                    <th scope="col" className="px-2 text-right">
                      Net at gate
                    </th>
                    <th scope="col" className="px-2 text-right">
                      Net now
                    </th>
                    <th scope="col" className="px-2 text-left">
                      Status
                    </th>
                    <th scope="col" className="px-4 text-right">
                      Weights
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lot.bags.map((b) => (
                    <Fragment key={b.id}>
                      <tr className="border-b border-rule/70">
                        <td className="px-4 py-2">
                          <Code>{b.tag ?? b.conflictTag ?? '—'}</Code>
                          {b.conflictTag && (
                            <span className="ml-1 text-[11px] text-danger">conflict</span>
                          )}
                        </td>
                        <td className="px-2 font-mono text-[13px]">{b.sackTypeCode ?? '—'}</td>
                        <td className="px-2 text-right">
                          <Kg grams={b.grossG} />
                        </td>
                        <td className="px-2 text-right">
                          <Kg grams={b.netG} />
                        </td>
                        <td className="px-2 text-right">
                          <Kg grams={b.netNowG} />
                        </td>
                        <td className="px-2">
                          <span className="flex flex-wrap gap-1">
                            <Status status={b.status} />
                            {b.hold !== 'NONE' && <Status status={b.hold} />}
                            {b.hold === 'QUARANTINE' && (
                              <BagHoldClear bagId={b.id} tag={b.tag ?? b.conflictTag ?? 'bag'} />
                            )}
                            {b.stream === 'CONV' && <Stream stream="CONV" />}
                          </span>
                        </td>
                        <td className="px-4 text-right">
                          <button
                            type="button"
                            aria-expanded={openBag === b.id}
                            className="font-mono text-[12px] underline decoration-rule underline-offset-2 hover:decoration-amber"
                            onClick={() => setOpenBag(openBag === b.id ? null : b.id)}
                          >
                            {b.weights.length} {openBag === b.id ? '▴' : '▾'}
                          </button>
                        </td>
                      </tr>
                      {openBag === b.id && (
                        <tr className="border-b border-rule/70 bg-paper-tint/40">
                          <td colSpan={7} className="px-6 py-3">
                            <Timeline
                              items={b.weights.map((w) => ({
                                id: w.id,
                                at: w.takenAt,
                                title: (
                                  <span className="font-mono tabular">
                                    {w.valueG !== null
                                      ? fmt.weight(w.valueG)
                                      : `${w.value ?? '—'} ${w.uom}`}
                                    {w.current && w.context === 'PURCHASE' && (
                                      <span className="ml-2 text-[11px] text-ok">counts</span>
                                    )}
                                    {w.supersedesId && (
                                      <span className="ml-2 text-[11px] text-muted">
                                        supersedes an earlier weight
                                      </span>
                                    )}
                                  </span>
                                ),
                                detail: (
                                  <span className="flex flex-wrap items-center gap-2">
                                    {humanize(w.context ?? w.kind)} ·{' '}
                                    {w.method ? humanize(w.method) : 'scale'} ·{' '}
                                    <UserName id={w.takenById} />
                                    {w.photo && (
                                      <PhotoViewer photo={w.photo} alt="Weight photo" size="sm" />
                                    )}
                                  </span>
                                ),
                                tone: w.current && w.context === 'PURCHASE' ? 'ok' : 'neutral',
                              }))}
                            />
                            {b.convReason && (
                              <p className="mt-2 text-[13px] text-muted">
                                Ordinary because: {b.convReason}
                              </p>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {can('custody-intervals:read') && <CustodySection lot={lot} />}

          <Section title="Money" eyebrow="Ledger · payouts" bodyClassName="p-0">
            <div className="grid grid-cols-1 divide-y divide-rule md:grid-cols-2 md:divide-x md:divide-y-0">
              <div className="p-4">
                <div className="eyebrow mb-2">Ledger lines</div>
                {lot.ledger.length === 0 ? (
                  <p className="text-[14px] text-muted">No ledger lines.</p>
                ) : (
                  <ul className="flex flex-col gap-1.5">
                    {lot.ledger.map((l) => (
                      <li
                        key={l.id}
                        className="flex items-baseline justify-between gap-2 text-[14px]"
                      >
                        <span>
                          {humanize(l.kind)}{' '}
                          <span className="text-[12px] text-muted">
                            <When at={l.occurredAt} dateOnly />
                          </span>
                        </span>
                        <Money paise={l.amountPaise} signed />
                      </li>
                    ))}
                    <li className="mt-1 flex items-baseline justify-between border-t border-rule pt-1.5 text-[14px] font-semibold">
                      <span>Balance on the lot</span>
                      <Money paise={lot.ledgerBalancePaise} />
                    </li>
                  </ul>
                )}
              </div>
              <div className="p-4">
                <div className="eyebrow mb-2">Payouts</div>
                {lot.payouts.length === 0 ? (
                  <p className="text-[14px] text-muted">No payout yet.</p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {lot.payouts.map((p) => (
                      <li key={p.id}>
                        <Link
                          to={`/c/payouts?open=${p.id}`}
                          className="flex items-center justify-between gap-2 rounded-chip px-1 py-0.5 hover:bg-paper-tint"
                        >
                          <span className="flex items-center gap-2 text-[14px]">
                            <span className="font-mono text-[12px] text-muted">
                              {p.kind === 'TOP_UP' ? 'Top-up' : 'Lot'} #{p.attemptNo}
                            </span>
                            <Status status={p.status} />
                          </span>
                          <Money paise={p.amountPaise} />
                        </Link>
                        {p.holdReason && (
                          <div className="pl-1 text-[12px] text-muted">
                            {payHoldLabel(p.holdReason)}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </Section>

          <Section
            title="Exceptions"
            eyebrow={`${lot.exceptions.filter((e) => e.status === 'OPEN').length} open`}
            bodyClassName="p-0"
          >
            <ExceptionTable rows={lot.exceptions} />
          </Section>
        </>
      }
      side={
        <>
          <Section title="Farmer">
            <Facts
              columns={2}
              items={[
                {
                  label: 'Name',
                  value: lot.farmerId ? (
                    <Link className="underline" to={`/c/farmers/${lot.farmerId}`}>
                      {lot.farmerName ?? 'Farmer'}
                    </Link>
                  ) : (
                    '—'
                  ),
                },
                { label: 'Roster no.', value: lot.rosterNo ?? '—', mono: true },
                { label: 'Crop', value: lot.cropName ?? '—' },
                {
                  label: 'Day',
                  value: lot.sessionId ? (
                    <RefLink to={`/c/sessions/${lot.sessionId}`}>
                      {lot.sessionCode ?? 'day'}
                    </RefLink>
                  ) : (
                    '—'
                  ),
                },
                { label: 'Committed by', value: <UserName id={lot.committedById} /> },
                { label: 'Operator', value: <UserName id={lot.sessionOperatorId} /> },
              ]}
            />
            {lot.siblings.length > 0 && (
              <div className="mt-3 border-t border-rule pt-3">
                <div className="eyebrow mb-1.5">Same delivery</div>
                <ul className="flex flex-wrap gap-2">
                  {lot.siblings.map((s) => (
                    <li key={s.id}>
                      <RefLink to={`/c/lots/${s.id}`}>{s.code}</RefLink>{' '}
                      <span className="text-[12px] text-muted">
                        {s.stream === 'CONV' ? 'ordinary' : 'good'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Section>

          <Section
            title="Slip"
            eyebrow={
              lot.receiptSlip
                ? `Book ${lot.receiptSlip.bookNo} · slip ${lot.receiptSlip.slipNo}`
                : lot.slipNo
                  ? `Slip ${lot.slipNo}`
                  : undefined
            }
          >
            <div className="flex flex-col gap-3">
              <PhotoViewer
                photo={lot.receiptSlip?.photo ?? lot.slipPhoto}
                alt={`Slip of ${lot.code}`}
                size="inline"
              />
              {lot.receiptSlip && (
                <Facts
                  columns={2}
                  items={[
                    { label: 'Typed by', value: <UserName id={lot.receiptSlip.enteredById} /> },
                    ...(v1
                      ? []
                      : [
                          {
                            label: 'Review',
                            value: lot.receiptSlip.reviewResult ? (
                              <Status status={lot.receiptSlip.reviewResult} />
                            ) : (
                              'Not reviewed'
                            ),
                          },
                        ]),
                  ]}
                />
              )}
              {!v1 && lot.deliveredByPhoto && (
                <div>
                  <div className="eyebrow mb-1.5">Brought by</div>
                  <PhotoViewer
                    photo={lot.deliveredByPhoto}
                    alt="Person who brought the produce"
                    size="md"
                  />
                </div>
              )}
            </div>
          </Section>

          <Section title="Sample" eyebrow="Lot retained">
            {lot.sample ? (
              <div className="flex flex-col gap-3">
                <Facts
                  columns={2}
                  items={[
                    {
                      label: 'Seal',
                      value: lot.sample.seal ?? lot.sample.conflictSeal ?? '—',
                      mono: true,
                    },
                    {
                      label: 'Weight',
                      value: lot.sample.weightG !== null ? `${lot.sample.weightG} g` : '—',
                      mono: true,
                    },
                    { label: 'Status', value: <Status status={lot.sample.status} /> },
                    { label: 'Keep until', value: <When at={lot.sample.retainUntil} dateOnly /> },
                  ]}
                />
                {lot.sample.photo && <PhotoViewer photo={lot.sample.photo} alt="Sealed sample" />}
              </div>
            ) : (
              <p className="text-[14px] text-muted">No sample recorded.</p>
            )}
          </Section>

          {homeStorage && (
            <Section title="Home storage" eyebrow={`${homeStorage.formCode} · signed`}>
              <ul className="flex flex-col gap-1 text-[14px]">
                {Object.entries(homeStorage.answers).map(([k, v]) => (
                  <li key={k} className="flex justify-between gap-2">
                    <span className="text-ink-soft">{humanize(k)}</span>
                    <span className="font-mono">{String(v)}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {lot.readings.length > 0 && (
            <Section title="Readings" eyebrow="Moisture">
              <ul className="flex flex-col gap-1 text-[14px]">
                {lot.readings.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2">
                    <span className="text-ink-soft">{humanize(r.kind)}</span>
                    <span className="flex items-center gap-2">
                      <span className="font-mono">
                        {r.value} {r.uom === 'PCT' ? '%' : r.uom}
                      </span>
                      {r.photo && <PhotoViewer photo={r.photo} alt="Meter photo" size="sm" />}
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </>
      }
    />
  )
}

function CustodySection({ lot }: { lot: LotDetail }) {
  const q = useCustody(lot.id, Boolean(lot.committedAt))
  const [bagId, setBagId] = useState<string | 'all'>('all')
  const c = q.data
  const bags = c?.bags ?? []
  const shown = bagId === 'all' ? bags : bags.filter((b) => b.id === bagId)
  const items: TimelineItem[] = []
  for (const b of shown) {
    for (const i of b.intervals)
      items.push({
        id: `${b.id}-i-${i.from}`,
        at: i.from,
        until: i.to,
        span: true,
        title: (
          <>
            {i.path}{' '}
            {bagId === 'all' && (
              <span className="font-mono text-[11px] text-muted">
                · {b.qrPayload ?? b.id.slice(0, 6)}
              </span>
            )}
          </>
        ),
        detail: (
          <>
            {humanize(i.level)}
            {i.siteKind ? ` · ${humanize(i.siteKind)}` : ''} · moved by{' '}
            <UserName id={i.movedById} />
          </>
        ),
        tone: i.to ? 'neutral' : 'ok',
      })
    for (const t of b.trips)
      items.push({
        id: `${b.id}-t-${t.shipmentId}`,
        at: t.loadedAt,
        until: t.unloadedAt,
        span: true,
        title: (
          <>
            Trip <RefLink to={`/c/shipments/${t.shipmentId}`}>{t.code}</RefLink>{' '}
            {bagId === 'all' && (
              <span className="font-mono text-[11px] text-muted">
                · {b.qrPayload ?? b.id.slice(0, 6)}
              </span>
            )}
          </>
        ),
        detail: (
          <>
            {humanize(t.status)} · vehicle clean:{' '}
            {t.vehicleClean === null ? 'not asked' : t.vehicleClean ? 'yes' : 'no'}
          </>
        ),
        tone: t.vehicleClean === false ? 'danger' : 'info',
      })
  }
  items.sort((a, b) => (a.at ?? '').localeCompare(b.at ?? ''))
  return (
    <Section
      title="Custody"
      eyebrow="Places and trips"
      actions={
        bags.length > 1 && (
          <select
            aria-label="Bag"
            value={bagId}
            onChange={(e) => setBagId(e.target.value)}
            className="h-8 rounded-chip border border-rule bg-surface px-2 text-[13px]"
          >
            <option value="all">All bags</option>
            {bags.map((b) => (
              <option key={b.id} value={b.id}>
                {b.qrPayload ?? b.id.slice(0, 8)}
              </option>
            ))}
          </select>
        )
      }
    >
      {!lot.committedAt ? (
        <p className="text-[14px] text-muted">Custody starts when the lot is committed.</p>
      ) : (
        <QueryState query={q} label="custody">
          {c && (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2 text-[14px]">
                Storage declared clean all along:{' '}
                {c.storageDeclared ? (
                  <Status status="OK" label="Yes" />
                ) : (
                  <Status status="HOLD" label="No" />
                )}
              </div>
              {c.failingSpans.length > 0 && (
                <Banner tone="warn" title="Spans that break the custody rules">
                  <ul className="list-disc pl-4">
                    {c.failingSpans.slice(0, 8).map((f, i) => (
                      <li key={i}>
                        {CUSTODY_REASON_LABEL[f.reason] ?? exceptionLabel(f.reason).en} ·{' '}
                        <When at={f.from} /> → {f.to ? <When at={f.to} /> : 'now'}
                      </li>
                    ))}
                  </ul>
                </Banner>
              )}
              <Timeline items={items} empty="No custody recorded yet." />
            </div>
          )}
        </QueryState>
      )}
    </Section>
  )
}
